/**
 * AnomalySystem.js
 *
 * Phase 13 (P3) — Survey anomalies for the `survey_anomalies` contract.
 *
 * Renders a deterministic set of anomaly POIs scattered around the body
 * (~8 anomalies seeded from the body name). Each anomaly is a glowing teal
 * pyramid floating slightly above the terrain. The player approaches within
 * 6m and presses E to "scan" the anomaly — we POST /api/player/survey and
 * mark the anomaly as scanned (different color + radar ping removed).
 *
 * Public API:
 *   setBody(body)        // build/clear anomalies for the current body
 *   setScanned(ids)      // mark scanned anomalies persistent across sessions
 *   tick(dt)             // per-frame proximity + bob animation
 *   getNearAnomaly()     // returns { id, distance } or null
 *   markScanned(id)      // local-only, called after server success
 *   getRadarPois()       // [{ id, kind:'anomaly', x, z }]
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

const ANOMALY_COUNT = 8;
const SCAN_RADIUS = 6.0;
const SPAWN_RING_MIN = 50;
const SPAWN_RING_MAX = 220;

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xc0ff33;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function hashStr(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export default class AnomalySystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this._root = new TransformNode('anomalyRoot', scene);
    this._anomalies = [];     // [{ id, pos, mesh, mat, scanned }]
    this._scanned = new Set();
    this._bodyKey = null;
    this._t = 0;
    this._scanRangeMult = 1;
  }

  /** Phase 1 — deep-scanner module widens the survey reach. */
  setScanRangeMult(m) {
    this._scanRangeMult = Math.max(1, Number(m) || 1);
  }

  setBody(body) {
    this.clear();
    if (!body) return;
    // Skip Gas Giant — no surface to scan.
    if (body.type === 'Gas Giant') return;
    const key = `${body.coords || 'k'}:${body.currentPlanetIdx ?? 0}:${body.currentMoonIdx ?? 'x'}`;
    this._bodyKey = key;
    const seed = hashStr(`${body.name || ''}|${body.type || ''}|${body.currentPlanetIdx ?? 0}`);
    const rng = mkRng(seed);
    for (let i = 0; i < ANOMALY_COUNT; i++) {
      const ang = rng() * Math.PI * 2;
      const r = SPAWN_RING_MIN + rng() * (SPAWN_RING_MAX - SPAWN_RING_MIN);
      const x = Math.cos(ang) * r;
      const z = Math.sin(ang) * r;
      const y = this.getTerrainHeight(x, z) + 1.2;
      const id = `${key}#anom${i}`;
      const mesh = this._buildAnomalyMesh(id, i);
      mesh.position.set(x, y, z);
      const mat = mesh.material;
      this._anomalies.push({ id, pos: new Vector3(x, y, z), mesh, mat, scanned: false });
    }
  }

  _buildAnomalyMesh(id, idx) {
    // A small floating pyramid (cone with low tessellation) tinted teal
    const mesh = MeshBuilder.CreateCylinder(`anom_${idx}`, {
      diameterTop: 0,
      diameterBottom: 1.6,
      height: 2.2,
      tessellation: 4,
    }, this.scene);
    mesh.parent = this._root;
    mesh.isPickable = false;
    mesh.rotation.y = idx * 0.7;
    const mat = new StandardMaterial(`anomMat_${idx}`, this.scene);
    mat.diffuseColor = new Color3(0.1, 0.5, 0.55);
    mat.emissiveColor = new Color3(0.3, 0.9, 1.0);
    mat.specularColor = new Color3(0, 0, 0);
    mat.alpha = 0.85;
    mesh.material = mat;
    // Track id on the mesh
    mesh.metadata = { anomalyId: id };
    return mesh;
  }

  clear() {
    for (const a of this._anomalies) {
      try { a.mat?.dispose?.(); } catch (_e) { /* noop */ }
      try { a.mesh.dispose(); } catch (_e) { /* noop */ }
    }
    this._anomalies = [];
    this._scanned = new Set();
    this._bodyKey = null;
  }

  /** Mark a set of anomaly ids as already scanned (e.g. from server state). */
  setScanned(ids) {
    this._scanned = new Set(ids || []);
    for (const a of this._anomalies) {
      if (this._scanned.has(a.id)) {
        a.scanned = true;
        if (a.mat) {
          a.mat.emissiveColor = new Color3(0.1, 0.3, 0.2);
          a.mat.alpha = 0.45;
        }
      }
    }
  }

  /** Locally mark scanned after server confirmation. */
  markScanned(id) {
    this._scanned.add(id);
    const a = this._anomalies.find((x) => x.id === id);
    if (a) {
      a.scanned = true;
      if (a.mat) {
        a.mat.emissiveColor = new Color3(0.1, 0.3, 0.2);
        a.mat.alpha = 0.45;
      }
    }
  }

  getNearAnomaly() {
    if (this._anomalies.length === 0) return null;
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = SCAN_RADIUS * (this._scanRangeMult || 1);
    for (const a of this._anomalies) {
      if (a.scanned) continue;
      const d = Math.hypot(p.x - a.pos.x, p.z - a.pos.z);
      if (d < bestDist) {
        bestDist = d;
        best = { id: a.id, distance: d };
      }
    }
    return best;
  }

  getRadarPois() {
    return this._anomalies
      .filter((a) => !a.scanned)
      .map((a) => ({ id: a.id, kind: 'anomaly', x: a.pos.x, z: a.pos.z }));
  }

  tick(dt) {
    this._t += dt;
    // Bob anomalies gently
    for (let i = 0; i < this._anomalies.length; i++) {
      const a = this._anomalies[i];
      if (!a.mesh) continue;
      a.mesh.rotation.y += dt * 0.6;
      a.mesh.position.y = a.pos.y + Math.sin(this._t * 1.5 + i) * 0.18;
    }
  }

  dispose() {
    this.clear();
    try { this._root.dispose(); } catch (_e) { /* noop */ }
  }
}
